"use client";

import { useEffect, useMemo, useState } from "react";

const formatNumber = (value: number) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 2 });

function useCompactViewport() {
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 720px)");
    const updateViewport = () => setIsCompact(mediaQuery.matches);

    updateViewport();
    mediaQuery.addEventListener("change", updateViewport);
    return () => mediaQuery.removeEventListener("change", updateViewport);
  }, []);

  return isCompact;
}

export default function CalculatorPage() {
  const isCompact = useCompactViewport();

  // DCA Calculator
  const [dcaInitialQty, setDcaInitialQty] = useState("");
  const [dcaInitialPrice, setDcaInitialPrice] = useState("");
  const [dcaAdditionalQty, setDcaAdditionalQty] = useState("");
  const [dcaAdditionalPrice, setDcaAdditionalPrice] = useState("");

  const dcaCalculation = useMemo(() => {
    const initialQty = Number(dcaInitialQty) || 0;
    const initialPrice = Number(dcaInitialPrice) || 0;
    const additionalQty = Number(dcaAdditionalQty) || 0;
    const additionalPrice = Number(dcaAdditionalPrice) || 0;

    if (initialQty <= 0 || initialPrice <= 0) {
      return {
        initialInvestment: 0,
        additionalInvestment: 0,
        totalInvestment: 0,
        totalQuantity: 0,
        newAverage: 0,
        costReduction: 0,
        costReductionPct: 0,
      };
    }

    const initialInvestment = initialQty * initialPrice;
    const additionalInvestment = additionalQty * additionalPrice;
    const totalInvestment = initialInvestment + additionalInvestment;
    const totalQuantity = initialQty + additionalQty;
    const newAverage = totalQuantity > 0 ? totalInvestment / totalQuantity : 0;
    const costReduction = initialPrice - newAverage;
    const costReductionPct = initialPrice > 0 ? (costReduction / initialPrice) * 100 : 0;

    return {
      initialInvestment,
      additionalInvestment,
      totalInvestment,
      totalQuantity,
      newAverage,
      costReduction,
      costReductionPct,
    };
  }, [dcaInitialQty, dcaInitialPrice, dcaAdditionalQty, dcaAdditionalPrice]);

  // PSX Investment Return Calculator
  const [psxBuyPrice, setPsxBuyPrice] = useState("");
  const [psxSellPrice, setPsxSellPrice] = useState("");
  const [psxQuantity, setPsxQuantity] = useState("");
  const [commissionRate, setCommissionRate] = useState("0.15");
  const [salesTaxRate, setSalesTaxRate] = useState("15");
  const [cdcRate, setCdcRate] = useState("0.02");
  const [advanceTaxRate, setAdvanceTaxRate] = useState("0");

  const psxCalculation = useMemo(() => {
    const commRate = Number(commissionRate) || 0;
    const stRate = Number(salesTaxRate) || 0;
    const cdcFeeRate = Number(cdcRate) || 0;
    const advTaxRate = Number(advanceTaxRate) || 0;

    const buyPrice = Number(psxBuyPrice) || 0;
    const sellPrice = Number(psxSellPrice) || 0;
    const quantity = Number(psxQuantity) || 0;

    const investmentAmount = buyPrice * quantity;
    const saleAmount = sellPrice * quantity;

    if (investmentAmount <= 0) {
      return {
        investmentAmount: 0,
        saleAmount: 0,
        grossProfit: 0,
        buyCommission: 0,
        buySalesTax: 0,
        buyCDC: 0,
        totalBuyCharges: 0,
        sellCommission: 0,
        sellSalesTax: 0,
        sellCDC: 0,
        sellAdvanceTax: 0,
        totalSellCharges: 0,
        totalCharges: 0,
        netProfit: 0,
        effectiveReturn: 0,
      };
    }

    const grossProfit = saleAmount - investmentAmount;

    // Buy side charges
    const buyCommission = (investmentAmount * commRate) / 100;
    const buySalesTax = (buyCommission * stRate) / 100;
    const buyCDC = (investmentAmount * cdcFeeRate) / 100;
    const totalBuyCharges = buyCommission + buySalesTax + buyCDC;

    // Sell side charges
    const sellCommission = (saleAmount * commRate) / 100;
    const sellSalesTax = (sellCommission * stRate) / 100;
    const sellCDC = (saleAmount * cdcFeeRate) / 100;
    const sellAdvanceTax = (grossProfit * advTaxRate) / 100;
    const totalSellCharges = sellCommission + sellSalesTax + sellCDC + sellAdvanceTax;

    const totalCharges = totalBuyCharges + totalSellCharges;
    const netProfit = grossProfit - totalCharges;
    const effectiveReturn = (netProfit / investmentAmount) * 100;

    return {
      investmentAmount,
      saleAmount,
      grossProfit,
      buyCommission,
      buySalesTax,
      buyCDC,
      totalBuyCharges,
      sellCommission,
      sellSalesTax,
      sellCDC,
      sellAdvanceTax,
      totalSellCharges,
      totalCharges,
      netProfit,
      effectiveReturn,
    };
  }, [psxBuyPrice, psxSellPrice, psxQuantity, commissionRate, salesTaxRate, cdcRate, advanceTaxRate]);

  return (
    <>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <h1 className="page-title">Calculator</h1>
          <p className="page-subtitle">DCA averaging and PSX investment returns with transaction charges</p>
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          <div style={{ display: "grid", gap: "1.5rem" }}>
            <div style={{ border: "1px solid var(--color-border)", borderRadius: "12px", padding: "1.5rem" }}>
              <h2 style={{ marginTop: 0 }}>DCA (Dollar Cost Averaging) Calculator</h2>
              <p style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", marginTop: "0.5rem", marginBottom: "1.5rem" }}>
                Calculate your new average cost when buying additional shares at a different price
              </p>

              <div style={{ display: "grid", gap: "1.5rem", gridTemplateColumns: isCompact ? "1fr" : "2fr 1fr" }}>
                <div style={{ background: "rgba(255,255,255,0.02)", padding: isCompact ? "1rem" : "1.5rem", borderRadius: "8px", border: "1px solid var(--color-border)", minWidth: 0 }}>
                  <h4 style={{ margin: "0 0 1rem 0", fontSize: "1rem", fontWeight: "600", color: "#3b82f6" }}>Initial Position</h4>
                  <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr" }}>
                    <div>
                      <label style={{ display: "block", marginBottom: "0.5rem", color: "var(--color-text)", fontSize: "0.9rem", fontWeight: "500" }}>
                        Initial Quantity
                      </label>
                      <input
                        value={dcaInitialQty}
                        onChange={(e) => setDcaInitialQty(e.target.value)}
                        type="number"
                        placeholder="1000"
                        style={{ width: "100%", padding: "0.65rem 0.75rem", borderRadius: "6px", border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text)", fontSize: "0.95rem" }}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", marginBottom: "0.5rem", color: "var(--color-text)", fontSize: "0.9rem", fontWeight: "500" }}>
                        Initial Price (Rs)
                      </label>
                      <input
                        value={dcaInitialPrice}
                        onChange={(e) => setDcaInitialPrice(e.target.value)}
                        type="number"
                        step="0.01"
                        placeholder="66.00"
                        style={{ width: "100%", padding: "0.65rem 0.75rem", borderRadius: "6px", border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text)", fontSize: "0.95rem" }}
                      />
                    </div>
                  </div>
                  {dcaCalculation.initialInvestment > 0 && (
                    <div style={{ marginTop: "0.75rem", padding: "0.75rem", background: "rgba(59, 130, 246, 0.1)", borderRadius: "6px" }}>
                      <span style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>Initial Investment: </span>
                      <strong style={{ color: "var(--color-text)", fontSize: "0.95rem" }}>Rs {formatNumber(dcaCalculation.initialInvestment)}</strong>
                    </div>
                  )}

                  <h4 style={{ margin: "1.5rem 0 1rem 0", fontSize: "1rem", fontWeight: "600", color: "var(--color-success)" }}>Additional Purchase</h4>
                  <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr" }}>
                    <div>
                      <label style={{ display: "block", marginBottom: "0.5rem", color: "var(--color-text)", fontSize: "0.9rem", fontWeight: "500" }}>
                        Additional Quantity
                      </label>
                      <input
                        value={dcaAdditionalQty}
                        onChange={(e) => setDcaAdditionalQty(e.target.value)}
                        type="number"
                        placeholder="500"
                        style={{ width: "100%", padding: "0.65rem 0.75rem", borderRadius: "6px", border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text)", fontSize: "0.95rem" }}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", marginBottom: "0.5rem", color: "var(--color-text)", fontSize: "0.9rem", fontWeight: "500" }}>
                        Current Price (Rs)
                      </label>
                      <input
                        value={dcaAdditionalPrice}
                        onChange={(e) => setDcaAdditionalPrice(e.target.value)}
                        type="number"
                        step="0.01"
                        placeholder="55.00"
                        style={{ width: "100%", padding: "0.65rem 0.75rem", borderRadius: "6px", border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text)", fontSize: "0.95rem" }}
                      />
                    </div>
                  </div>
                  {dcaCalculation.additionalInvestment > 0 && (
                    <div style={{ marginTop: "0.75rem", padding: "0.75rem", background: "rgba(16, 185, 129, 0.1)", borderRadius: "6px" }}>
                      <span style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>Additional Investment: </span>
                      <strong style={{ color: "var(--color-text)", fontSize: "0.95rem" }}>Rs {formatNumber(dcaCalculation.additionalInvestment)}</strong>
                    </div>
                  )}
                </div>

                <div style={{ background: "rgba(255,255,255,0.02)", padding: isCompact ? "1rem" : "1.5rem", borderRadius: "8px", border: "1px solid var(--color-border)", minWidth: 0 }}>
                  <h4 style={{ margin: "0 0 1.25rem 0", fontSize: "1.1rem", fontWeight: "600" }}>Result</h4>

                  {dcaCalculation.initialInvestment > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                      <div>
                        <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", marginBottom: "0.25rem" }}>
                          Total Investment
                        </div>
                        <div style={{ color: "var(--color-text)", fontSize: "1.25rem", fontWeight: "600" }}>
                          Rs {formatNumber(dcaCalculation.totalInvestment)}
                        </div>
                      </div>

                      <div>
                        <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", marginBottom: "0.25rem" }}>
                          Total Quantity
                        </div>
                        <div style={{ color: "var(--color-text)", fontSize: "1.25rem", fontWeight: "600" }}>
                          {formatNumber(dcaCalculation.totalQuantity)} shares
                        </div>
                      </div>

                      <div style={{ paddingTop: "0.75rem", borderTop: "1px solid var(--color-border)" }}>
                        <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
                          New Average Cost
                        </div>
                        <div style={{ color: "#3b82f6", fontSize: "1.75rem", fontWeight: "700" }}>
                          Rs {formatNumber(dcaCalculation.newAverage)}
                        </div>
                      </div>

                      {dcaCalculation.costReduction !== 0 && (
                        <div style={{ background: dcaCalculation.costReduction > 0 ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)", padding: "1rem", borderRadius: "6px", marginTop: "0.5rem" }}>
                          <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
                            {dcaCalculation.costReduction > 0 ? "Cost Reduced By" : "Cost Increased By"}
                          </div>
                          <div style={{ color: dcaCalculation.costReduction > 0 ? "var(--color-success)" : "var(--color-danger)", fontSize: "1.3rem", fontWeight: "700" }}>
                            Rs {formatNumber(Math.abs(dcaCalculation.costReduction))}
                          </div>
                          <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", marginTop: "0.25rem" }}>
                            ({formatNumber(Math.abs(dcaCalculation.costReductionPct))}%)
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", textAlign: "center", padding: "2rem 0" }}>
                      Enter initial position to calculate average cost
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ border: "1px solid var(--color-border)", borderRadius: "12px", padding: "1.5rem" }}>
              <h2 style={{ marginTop: 0, fontSize: "1.5rem" }}>PSX Investment Return Calculator</h2>
              <p style={{ color: "var(--color-text-muted)", fontSize: "0.95rem", marginTop: "0.5rem", marginBottom: "1.5rem" }}>
                Calculate your actual net profit after all PSX transaction taxes and charges
              </p>

              <div style={{ display: "grid", gap: "1.5rem", gridTemplateColumns: isCompact ? "1fr" : "2fr 1fr" }}>
                <div style={{ background: "rgba(255,255,255,0.02)", padding: isCompact ? "1rem" : "1.5rem", borderRadius: "8px", border: "1px solid var(--color-border)", minWidth: 0 }}>
                  <div style={{ display: "grid", gap: "1.25rem" }}>
                    <div style={{ display: "grid", gap: "0.75rem", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr" }}>
                      <div>
                        <label style={{ display: "block", marginBottom: "0.5rem", color: "var(--color-text)", fontSize: "0.9rem", fontWeight: "500" }}>
                          Buy Price
                        </label>
                        <div style={{ position: "relative" }}>
                          <span style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>Rs</span>
                          <input
                            value={psxBuyPrice}
                            onChange={(e) => setPsxBuyPrice(e.target.value)}
                            type="number"
                            step="0.01"
                            placeholder="45.50"
                            style={{ width: "100%", padding: "0.65rem 0.75rem 0.65rem 2.25rem", borderRadius: "6px", border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text)", fontSize: "0.95rem" }}
                          />
                        </div>
                      </div>
                      <div>
                        <label style={{ display: "block", marginBottom: "0.5rem", color: "var(--color-text)", fontSize: "0.9rem", fontWeight: "500" }}>
                          Sell Price
                        </label>
                        <div style={{ position: "relative" }}>
                          <span style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>Rs</span>
                          <input
                            value={psxSellPrice}
                            onChange={(e) => setPsxSellPrice(e.target.value)}
                            type="number"
                            step="0.01"
                            placeholder="52.00"
                            style={{ width: "100%", padding: "0.65rem 0.75rem 0.65rem 2.25rem", borderRadius: "6px", border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text)", fontSize: "0.95rem" }}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label style={{ display: "block", marginBottom: "0.5rem", color: "var(--color-text)", fontSize: "0.9rem", fontWeight: "500" }}>
                        Quantity
                      </label>
                      <input
                        value={psxQuantity}
                        onChange={(e) => setPsxQuantity(e.target.value)}
                        type="number"
                        placeholder="1000"
                        style={{ width: "100%", padding: "0.65rem 0.75rem", borderRadius: "6px", border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text)", fontSize: "0.95rem" }}
                      />
                    </div>

                    <div style={{ paddingTop: "0.5rem", borderTop: "1px solid var(--color-border)" }}>
                      <details style={{ cursor: "pointer" }}>
                        <summary style={{ fontWeight: "500", fontSize: "0.9rem", color: "var(--color-text)", padding: "0.5rem 0" }}>
                          Advanced Settings (Commission & Taxes)
                        </summary>
                        <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr", marginTop: "1rem" }}>
                          <div>
                            <label style={{ display: "block", marginBottom: "0.5rem", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                              Commission Rate (%)
                            </label>
                            <input
                              value={commissionRate}
                              onChange={(e) => setCommissionRate(e.target.value)}
                              type="number"
                              step="0.01"
                              placeholder="0.15"
                              style={{ width: "100%", padding: "0.6rem", borderRadius: "6px", border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text)", fontSize: "0.9rem" }}
                            />
                          </div>
                          <div>
                            <label style={{ display: "block", marginBottom: "0.5rem", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                              Sales Tax (%)
                            </label>
                            <input
                              value={salesTaxRate}
                              onChange={(e) => setSalesTaxRate(e.target.value)}
                              type="number"
                              step="0.1"
                              placeholder="15"
                              style={{ width: "100%", padding: "0.6rem", borderRadius: "6px", border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text)", fontSize: "0.9rem" }}
                            />
                          </div>
                          <div>
                            <label style={{ display: "block", marginBottom: "0.5rem", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                              CDC Charges (%)
                            </label>
                            <input
                              value={cdcRate}
                              onChange={(e) => setCdcRate(e.target.value)}
                              type="number"
                              step="0.01"
                              placeholder="0.02"
                              style={{ width: "100%", padding: "0.6rem", borderRadius: "6px", border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text)", fontSize: "0.9rem" }}
                            />
                          </div>
                          <div>
                            <label style={{ display: "block", marginBottom: "0.5rem", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                              Advance Tax (%)
                            </label>
                            <input
                              value={advanceTaxRate}
                              onChange={(e) => setAdvanceTaxRate(e.target.value)}
                              type="number"
                              step="0.1"
                              placeholder="0"
                              style={{ width: "100%", padding: "0.6rem", borderRadius: "6px", border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text)", fontSize: "0.9rem" }}
                            />
                          </div>
                        </div>
                      </details>
                    </div>
                  </div>
                </div>

                <div style={{ background: "rgba(255,255,255,0.02)", padding: "1.5rem", borderRadius: "8px", border: "1px solid var(--color-border)" }}>
                  <h4 style={{ margin: "0 0 1.25rem 0", fontSize: "1.1rem", fontWeight: "600" }}>Summary</h4>

                  {psxCalculation.investmentAmount > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                      <div>
                        <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", marginBottom: "0.25rem" }}>
                          Investment Amount
                        </div>
                        <div style={{ color: "var(--color-text)", fontSize: "1.25rem", fontWeight: "600" }}>
                          Rs {formatNumber(psxCalculation.investmentAmount)}
                        </div>
                      </div>

                      <div>
                        <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", marginBottom: "0.25rem" }}>
                          Sale Amount
                        </div>
                        <div style={{ color: "var(--color-text)", fontSize: "1.25rem", fontWeight: "600" }}>
                          Rs {formatNumber(psxCalculation.saleAmount)}
                        </div>
                      </div>

                      <div style={{ paddingTop: "0.75rem", borderTop: "1px solid var(--color-border)" }}>
                        <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", marginBottom: "0.25rem" }}>
                          Gross Profit
                        </div>
                        <div style={{ color: "var(--color-success)", fontSize: "1.15rem", fontWeight: "600" }}>
                          Rs {formatNumber(psxCalculation.grossProfit)}
                        </div>
                      </div>

                      <div>
                        <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", marginBottom: "0.25rem" }}>
                          Total Transaction Charges
                        </div>
                        <div style={{ color: "var(--color-danger)", fontSize: "1.15rem", fontWeight: "600" }}>
                          Rs {formatNumber(psxCalculation.totalCharges)}
                        </div>
                      </div>

                      <div style={{ background: "rgba(16, 185, 129, 0.1)", padding: "1rem", borderRadius: "6px", marginTop: "0.5rem" }}>
                        <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
                          Net Profit
                        </div>
                        <div style={{ color: psxCalculation.netProfit >= 0 ? "var(--color-success)" : "var(--color-danger)", fontSize: "1.75rem", fontWeight: "700" }}>
                          Rs {formatNumber(psxCalculation.netProfit)}
                        </div>
                        <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", marginTop: "0.5rem" }}>
                          Effective Return: <strong style={{ color: psxCalculation.effectiveReturn >= 0 ? "var(--color-success)" : "var(--color-danger)" }}>
                            {formatNumber(psxCalculation.effectiveReturn)}%
                          </strong>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", textAlign: "center", padding: "2rem 0" }}>
                      Enter buy price, sell price, and quantity to see calculation
                    </div>
                  )}
                </div>
              </div>

              {psxCalculation.investmentAmount > 0 && (
                <div style={{ marginTop: "1.5rem", paddingTop: "1.5rem", borderTop: "1px solid var(--color-border)" }}>
                  <h4 style={{ margin: "0 0 1rem 0", fontSize: "1.1rem", fontWeight: "600" }}>Breakdown</h4>
                  <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr" }}>
                    <div style={{ background: "rgba(255,255,255,0.02)", padding: "1rem", borderRadius: "6px", border: "1px solid var(--color-border)" }}>
                      <div style={{ fontWeight: "600", marginBottom: "0.75rem", color: "var(--color-text)", fontSize: "0.95rem" }}>
                        Buy Side Charges
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", fontSize: "0.85rem" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", color: "var(--color-text-muted)" }}>
                          <span>Commission</span>
                          <span style={{ color: "var(--color-text)" }}>Rs {formatNumber(psxCalculation.buyCommission)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", color: "var(--color-text-muted)" }}>
                          <span>Sales Tax</span>
                          <span style={{ color: "var(--color-text)" }}>Rs {formatNumber(psxCalculation.buySalesTax)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", color: "var(--color-text-muted)" }}>
                          <span>CDC Charges</span>
                          <span style={{ color: "var(--color-text)" }}>Rs {formatNumber(psxCalculation.buyCDC)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "0.5rem", borderTop: "1px solid var(--color-border)", fontWeight: "600" }}>
                          <span style={{ color: "var(--color-text)" }}>Total</span>
                          <span style={{ color: "var(--color-danger)" }}>Rs {formatNumber(psxCalculation.totalBuyCharges)}</span>
                        </div>
                      </div>
                    </div>

                    <div style={{ background: "rgba(255,255,255,0.02)", padding: "1rem", borderRadius: "6px", border: "1px solid var(--color-border)" }}>
                      <div style={{ fontWeight: "600", marginBottom: "0.75rem", color: "var(--color-text)", fontSize: "0.95rem" }}>
                        Sell Side Charges
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", fontSize: "0.85rem" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", color: "var(--color-text-muted)" }}>
                          <span>Commission</span>
                          <span style={{ color: "var(--color-text)" }}>Rs {formatNumber(psxCalculation.sellCommission)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", color: "var(--color-text-muted)" }}>
                          <span>Sales Tax</span>
                          <span style={{ color: "var(--color-text)" }}>Rs {formatNumber(psxCalculation.sellSalesTax)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", color: "var(--color-text-muted)" }}>
                          <span>CDC Charges</span>
                          <span style={{ color: "var(--color-text)" }}>Rs {formatNumber(psxCalculation.sellCDC)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", color: "var(--color-text-muted)" }}>
                          <span>Advance Tax</span>
                          <span style={{ color: "var(--color-text)" }}>Rs {formatNumber(psxCalculation.sellAdvanceTax)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "0.5rem", borderTop: "1px solid var(--color-border)", fontWeight: "600" }}>
                          <span style={{ color: "var(--color-text)" }}>Total</span>
                          <span style={{ color: "var(--color-danger)" }}>Rs {formatNumber(psxCalculation.totalSellCharges)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
